import {
  SUPPORTED_HOME_FEATURE_NAMES,
  HOME_FEATURE_CATEGORIES,
  HOME_FEATURES_LIST,
} from 'accounts/constants/home-features';
import { isNil } from 'es-toolkit/compat';
import { AcFeatureTypes, ParkingTypes } from 'models/enums';

export function getHomeFeaturesList(isInspectionCompleted: any) {
  const featuresList = [...HOME_FEATURES_LIST];

  if (isInspectionCompleted) {
    return featuresList.map((feature) => ({ ...feature, disabled: true }));
  }

  return featuresList;
}

type FeatureProps = {
  name: string;
  isAvailable: boolean;
  attributes: {
    isCentralAirCondition?: boolean;
    // An array, not the one-element tuple this used to be — a home can have any
    // number of parking spots. Both fields are optional because the default
    // branch below `delete`s each of them.
    spots?: { hasAssignedNumber?: boolean; spotNumber?: string }[];
  };
  uniqueId?: string;
  category?: string;
  isAppliance?: boolean;
};

/**
 * `attributes` is always populated while this builds the object, but both
 * setup-flow feature steps `delete` it again when it comes out empty — so it is
 * optional on what callers receive, and required on what is assembled here.
 */
type SubmittedFeature = Omit<FeatureProps, 'attributes'> &
  Partial<Pick<FeatureProps, 'attributes'>>;

export function getFeatureAttributes(feature: {
  name: string;
  attributes: unknown;
  uniqueId: string;
  type: string;
}): SubmittedFeature {
  const featureProps: FeatureProps = {
    name: feature.name,
    isAvailable: true,
    attributes: feature.attributes || {},
  };

  if (feature.uniqueId) {
    featureProps.uniqueId = feature.uniqueId;
  }

  if (SUPPORTED_HOME_FEATURE_NAMES.Parking.name === feature.name) {
    featureProps.isAppliance = false;
    featureProps.category = HOME_FEATURE_CATEGORIES.Parking;
    featureProps.name = feature.type;

    switch (feature.type) {
      case ParkingTypes.DrivewayParking:
        delete featureProps.attributes.spots;
        break;

      case ParkingTypes.OffStreetParking:
      case ParkingTypes.OnStreetParking:
        // `attributes` is required while this object is assembled and optional on
        // what the function returns, which is exactly what this branch produces.
        delete (featureProps as SubmittedFeature).attributes;
        break;

      default:
        if (featureProps.attributes?.spots?.length) {
          featureProps.attributes.spots.forEach((s) => {
            if (!isNil(s)) {
              if (!s.hasAssignedNumber) {
                delete s.spotNumber;
              }
              delete s.hasAssignedNumber;
            }
          });
        }
        break;
    }
  }

  if (SUPPORTED_HOME_FEATURE_NAMES.Storage.name === feature.name) {
    featureProps.isAppliance = false;
    featureProps.category = HOME_FEATURE_CATEGORIES.Storage;
  }

  if (SUPPORTED_HOME_FEATURE_NAMES.Heat.name === feature.name) {
    featureProps.isAppliance = true;
    featureProps.category = HOME_FEATURE_CATEGORIES['Climate Control'];
  }

  if (SUPPORTED_HOME_FEATURE_NAMES.Ac.name === feature.name) {
    featureProps.isAppliance = true;
    featureProps.category = HOME_FEATURE_CATEGORIES['Climate Control'];

    if (feature.type === AcFeatureTypes.CentralAc) {
      featureProps.attributes.isCentralAirCondition = true;
    } else {
      featureProps.attributes.isCentralAirCondition = false;
    }
  }

  if (SUPPORTED_HOME_FEATURE_NAMES.Laundry.name === feature.name) {
    featureProps.isAppliance = true;
    featureProps.category = HOME_FEATURE_CATEGORIES.Laundry;
  }

  if (
    [
      SUPPORTED_HOME_FEATURE_NAMES.Pool.name,
      SUPPORTED_HOME_FEATURE_NAMES.Jacuzzi.name,
    ].includes(feature.name)
  ) {
    featureProps.isAppliance = true;
    featureProps.category = HOME_FEATURE_CATEGORIES.Pool;
  }

  if (SUPPORTED_HOME_FEATURE_NAMES.SecuritySystem.name === feature.name) {
    featureProps.isAppliance = true;
    featureProps.category = HOME_FEATURE_CATEGORIES['Safety & Security'];
  }

  if (
    [
      SUPPORTED_HOME_FEATURE_NAMES['Trees/ Landscaping/ Garden'].name,
      SUPPORTED_HOME_FEATURE_NAMES.AutomaticSprinklers.name,
    ].includes(feature.name)
  ) {
    featureProps.isAppliance = false;
    featureProps.category = HOME_FEATURE_CATEGORIES.Yard;
  }

  return { ...featureProps };
}

export function getFeatureFormObject(feature: any) {
  const featureObject = {
    selected: !!feature.isAvailable,
    ...feature,
  };

  if (feature.name === SUPPORTED_HOME_FEATURE_NAMES.Ac.name) {
    featureObject.type = feature.attributes?.isCentralAirCondition
      ? AcFeatureTypes.CentralAc
      : AcFeatureTypes.WindowUnits;
  }

  if (Object.values(ParkingTypes).includes(feature.name) && feature.name) {
    featureObject.type = feature.name;

    if (featureObject.attributes?.spots?.length) {
      featureObject.attributes.spots.forEach((s: any) => {
        if (!s || !s.spotNumber) {
          s = {
            hasAssignedNumber: false,
          };
        } else if (s && s.spotNumber) {
          s.hasAssignedNumber = true;
        }
      });
    }
  }

  return { ...featureObject };
}
